import { SaveButton } from '@components/Buttons/SaveButton';
import { GridItem } from '@components/GridItem';
import { HorizontalGrid } from '@components/HorizontalGrid';
import { LoadingBox } from '@components/LoadingBox';
import { Main } from '@components/Main';
import { PageContent } from '@components/PageContent';
import { PageTitle, PageTitleText } from '@components/PageTitle';
import { useField } from '@hooks/useField';
import { useForm } from '@hooks/useForm';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { useValidation } from '@hooks/useValidation';
import { useValueState } from '@hooks/useValueState';
import { IVendedor } from '@model/Vendedor';
import { IVendedorEditar, useVendedorService } from '@services/VendedorService';
import { yup } from '@validations/Yup';
import { AxiosResponse } from 'axios';
import React from 'react';
import { VendedorFichaForm } from './Form';
import { FichaVendedorTab } from './Tab';

export type VendedorFichaProps = {
    vendedorId?: number
    onVendedorAtualizado: (vendedor: AxiosResponse<IVendedor>) => void
}

export function VendedorFicha ({ vendedorId, onVendedorAtualizado }: VendedorFichaProps) {
    const vendedor = useValueState<IVendedor>();

    const loading = useLoading();
    const message = useMessage();

    const vendedorNome = React.useMemo(() => vendedor.value?.nome !== undefined ? vendedor.value.nome : "", [vendedor.value])

    const vendedorService = useVendedorService();

    const form = useForm<IVendedorEditar>({
        nome: useField(),
        cpf: useField(),
        telefone: useField()
    })

    const validation = useValidation<IVendedorEditar>({
        nome: yup.string().trim().required(),
        cpf: yup.string().trim().required().validCPF(),
    });

    const handleSalvar = React.useCallback(() => {
        if (!loading.isLoading && vendedorId !== undefined) {
            const values = form.getInputValues();

            form.clearErrors();

            validation.validate(values)
                .then(() => {
                    loading.start();
                    vendedorService.editar(vendedorId, values)
                        .then(response => {
                            message.success("Vendedor editado com sucesso");
                            onVendedorAtualizado(response);
                        })
                        .catch(e => {
                            message.handleResponse(e)
                                .badRequest(form.setErrorsFromServer)
                                .showException();
                        })
                        .finally(loading.stop);
                })
                .catch(form.setErrorsFromYup);
        }
    }, [form, loading, message, onVendedorAtualizado, validation, vendedorId, vendedorService]);

    React.useEffect(() => {
        form.reset();

        if (!loading.isLoading && vendedorId !== undefined) {
            vendedorService.carregar(vendedorId)
                .then(response => {
                    form.setValues(response.data);
                    vendedor.set(response.data);
                })
                .catch(message.showException)
                .finally(loading.stop);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendedorId])

    return (
        <Main>
            <LoadingBox isLoading={loading.isLoading}>
                <PageTitle>
                    <HorizontalGrid>
                        <GridItem>
                            <PageTitleText text={`Vendedor ${vendedorNome}`} />
                        </GridItem>
                        <GridItem floatRight>
                            <SaveButton onClick={handleSalvar} />
                        </GridItem>
                    </HorizontalGrid>
                </PageTitle>

                <PageContent>
                    <VendedorFichaForm
                        form={form}
                    />

                    {
                        vendedor.value !== undefined && (
                            <FichaVendedorTab 
                                vendedor={vendedor.value}
                            />
                        )
                    }
                </PageContent>
            </LoadingBox>
        </Main>
    )
}